import React, {Fragment, useCallback, useEffect, useState} from 'react';
import {Box, Stepper, Step, StepButton, Button, Tooltip} from '@material-ui/core';
import {withTranslation} from 'react-i18next';
import {compose} from 'recompose';
import {withStyles} from '@material-ui/core';
import styles from './styles';
import {
  LoadingWrapperWithTimer,
  TemplateComparison,
  TemplateStopSelection,
  TemplateStopTemplateFilter,
  TextPlaceholderWrapper,
} from 'components';
import useShipperOptions from 'hooks/useShipperOptions';
import useCarriersWithHubs from 'hooks/useCarriersWithHubs';
import {displayModes} from 'services/enums/displayModes';
import {TemplateStopService} from 'services/backend/templateStopService';
import {getDayNumberByDayOfWeek} from 'services/enums/dayOfWeekHelper';
import PropTypes from 'prop-types';
import {
  GoogleMapsDistanceMatrixModeOption,
} from "../../services/enums/GoolgeMapsDistanceMatrixHelper";

function TemplateOptimization(props) {
  const {t, classes, displayMode} = props;

  const steps = [t('templateStopSelection'), t('templateComparison')];

  const {shipperOptions} = useShipperOptions();
  const {carriersWithHubs} = useCarriersWithHubs();
  const [filter, setFilter] = useState({
    shipperName: '',
    carrierName: '',
    microHubName: '',
    dayOfWeek: '',
  });
  const [backendLoadingTemplates, setBackendLoadingTemplates] = useState(false);
  const [backendLoadingTemplateStops, setBackendLoadingTemplateStops] =
    useState(false);
  const [backendLoadingFunctions, setBackendLoadingFunctions] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [optimizedTemplates, setOptimizedTemplates] = useState([]);
  const [templateStops, setTemplateStops] = useState([]);

  const [selectedAssignedTemplateStops, setSelectedAssignedTemplateStops] =
    useState([]);
  const [selectedUnassignedTemplateStops, setSelectedUnassignedTemplateStops] =
    useState([]);

  const [selectedOldTemplateIds, setSelectedOldTemplateIds] = useState([]);
  const [selectedOptimizedTemplateIds, setSelectedOptimizedTemplateIds] =
    useState([]);

  const [activeStep, setActiveStep] = React.useState(0);

  const [configureOptimizerAlgorithmDialogState, setConfigureOptimizerAlgorithmDialogState] = useState({
    open: false,
  });

  const [optimizerAlgorithmConfiguration, setOptimizerAlgorithmConfiguration] = useState({
    vehicleSpeed: 18,
    vehicleCapacity: 3,
    tourCount: 0,
    timeLimit: 300,
    useRelation: false,
    useManhattanDistance: false,
    travelMode: GoogleMapsDistanceMatrixModeOption.Bicycling, // for google maps
    departureTime: new Date(Date.now() + 60 * 60 * 1000), // for google maps
    useStopCapacitiesFromDb: true,
    shopCapacityDefaultValue: 1,
    chestCapacityDefaultValue: 1,
  });

  const StatusEnum = {
    Running: "Running",
    Done: "Done",
    Failed: "Failed",
  };

  const onConfigureOptimizerAlgorithmCancel = () => {
    setConfigureOptimizerAlgorithmDialogState(prev => ({
      ...prev,
      open: false,
    }));
  };

  const onConfigureOptimizerAlgorithmSave = (newConfig) => {
    setConfigureOptimizerAlgorithmDialogState(prev => ({
      ...prev,
      open: false,
    }));
    setOptimizerAlgorithmConfiguration(newConfig);
  };
  const openOptimizerAlgorithmDialog = () => {
    setConfigureOptimizerAlgorithmDialogState(prev => ({
      ...prev,
      open: true,
    }));
  };

  const isGoogleMapsDepartureDateInPast = () => {
    return optimizerAlgorithmConfiguration.useManhattanDistance &&
      optimizerAlgorithmConfiguration.departureTime <= new Date();
  }

  const optimizeTemplates = async () => {
    if (isGoogleMapsDepartureDateInPast()) {
      setTemplates([...templates]); // Trigger rerender without actual update
      alert(t("departureTimeMustBeInFuture"))
      return;
    }

    setBackendLoadingFunctions(true);
    try {
      const OptimizerConfiguration = {
        VehicleVelocityKilometersPerHour: optimizerAlgorithmConfiguration.vehicleSpeed,
        vehicleCapacity: optimizerAlgorithmConfiguration.vehicleCapacity,
        tourCount: optimizerAlgorithmConfiguration.tourCount,
        timeLimitInSeconds: optimizerAlgorithmConfiguration.timeLimit,
        useRelations: optimizerAlgorithmConfiguration.useRelation,
        useManhattanDistance: optimizerAlgorithmConfiguration.useManhattanDistance,
        travelMode: optimizerAlgorithmConfiguration.travelMode,
        departureTime: optimizerAlgorithmConfiguration.departureTime, 
        useStopCapacitiesFromDb: optimizerAlgorithmConfiguration.useStopCapacitiesFromDb,
        shopCapacityDefaultValue: optimizerAlgorithmConfiguration.shopCapacityDefaultValue,
        chestCapacityDefaultValue: optimizerAlgorithmConfiguration.chestCapacityDefaultValue,
      };
      const optimizeTemplateRequest = {
        templateStops: selectedUnassignedTemplateStops.concat(selectedAssignedTemplateStops),
        configuration: OptimizerConfiguration
      };

      const {jobIdentifier} = await TemplateStopService.optimizeTemplates(
        optimizeTemplateRequest
      );

      await getTemplates(jobIdentifier, filter)
      setBackendLoadingFunctions(false);

    } catch (error) {
      alert(`${t('errorGetOptimizedTemplateStopTemplates')}, ${error}`);
    }
  };

  async function getTemplates(jobIdentifier, filter) {
    let running = true;
    let status = '';
    while (running) {
      await sleep(5000);
      status = await TemplateStopService.getJobStatus(jobIdentifier);
      running = status === StatusEnum.Running;
    }

    if (status === StatusEnum.Done) {
      const {templates} = await TemplateStopService.getOptimizationResult(
        jobIdentifier,
        filter
      );
      setOptimizedTemplates(templates);
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
    if (status === StatusEnum.Failed) {
      alert(`${t('errorOptimizeTemplateStopTemplates')}`);
    }
  }

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  async function importSelectedTemplates() {
    if (window.confirm(t('confirmImportSelectedTemplates'))) {
      setBackendLoadingTemplates(true);
      const selectedOptimizedTemplates = optimizedTemplates.filter((template) =>
        selectedOptimizedTemplateIds.includes(template.tempId)
      );
      const numOfDeletedTemplates = await TemplateStopService.importTemplates(selectedOptimizedTemplates, filter);
      window.alert(
        `${t('importedTemplates')}: ${selectedOptimizedTemplateIds.length}, ${t('deletedTemplates')}: ${numOfDeletedTemplates}`
      );
      setBackendLoadingTemplates(false);
    }
  }

  const loadTemplates = useCallback(
    async (withLoadingIndicator = true) => {
      if (withLoadingIndicator) setBackendLoadingTemplates(true);
      try {
        const templates =
          await TemplateStopService.getTemplatesWithStopsAndDurationsByFilter({
            dayOfWeek: getDayNumberByDayOfWeek(filter.dayOfWeek),
            shipperName: filter.shipperName,
            carrierName: filter.carrierName,
            microHubName: filter.microHubName,
          });
        setTemplates(templates);
        if (withLoadingIndicator) setBackendLoadingTemplates(false);
      } catch (error) {
        alert(`${t('errorLoadingTourTemplates')}, ${error}`);
        if (withLoadingIndicator) setBackendLoadingTemplates(false);
      }
    },
    [filter, t]
  );

  const loadTemplateStops = useCallback(async () => {
    setBackendLoadingTemplateStops(true);
    try {
      const templateStops =
        await TemplateStopService.getTemplateStopsWithFilter(filter);
      setTemplateStops(templateStops);
      setOptimizerAlgorithmConfiguration(prev => ({
        ...prev,
        vehicleCount: Math.floor(templateStops.length / 2)
      }));
      setBackendLoadingTemplateStops(false);
    } catch (e) {
      alert(`${t('errorLoadingTemplateStops')}, ${e}`);
      setBackendLoadingTemplateStops(false);
    }
  }, [filter, t]);

  useEffect(() => {
    if (!filter.dayOfWeek || !filter.shipperName) return;
    loadTemplates();
    loadTemplateStops();
  }, [loadTemplates, loadTemplateStops, filter]);

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <TemplateStopSelection
            displayMode={displayMode}
            templates={templates}
            templateStops={templateStops}
            selectedAssignedTemplateStops={selectedAssignedTemplateStops}
            setSelectedAssignedTemplateStops={setSelectedAssignedTemplateStops}
            selectedUnassignedTemplateStops={selectedUnassignedTemplateStops}
            setSelectedUnassignedTemplateStops={
              setSelectedUnassignedTemplateStops
            }
            configureOptimizerAlgorithmDialogState={configureOptimizerAlgorithmDialogState}
            setConfigureOptimizerAlgorithmDialogState={setConfigureOptimizerAlgorithmDialogState}
            optimizerAlgorithmConfiguration={optimizerAlgorithmConfiguration}
            setOptimizerAlgorithmConfiguration={setOptimizerAlgorithmConfiguration}
            onConfigureOptimizerAlgorithmCancel={onConfigureOptimizerAlgorithmCancel}
            onConfigureOptimizerAlgorithmSave={onConfigureOptimizerAlgorithmSave}
            openOptimizerAlgorithmDialog={openOptimizerAlgorithmDialog}
          />
        );
      case 1:
        return (
          <TemplateComparison
            displayMode={displayMode}
            oldTemplates={templates}
            optimizedTemplates={optimizedTemplates}
            selectedOldTemplateIds={selectedOldTemplateIds}
            setSelectedOldTemplateIds={setSelectedOldTemplateIds}
            selectedOptimizedTemplateIds={selectedOptimizedTemplateIds}
            setSelectedOptimizedTemplateIds={setSelectedOptimizedTemplateIds}
          />
        );
      default:
        return 'Unknown step';
    }
  };

  function getTemplateStopSelectionButtons() {
    const isGoogleMapsBlocked = isGoogleMapsDepartureDateInPast();
    const isDisabled = (selectedAssignedTemplateStops.length === 0 && selectedUnassignedTemplateStops.length === 0) ||
      isGoogleMapsBlocked;

    return (
      <Fragment>
        <Button color="primary" disabled onClick={handleBack} sx={{mr: 1}}>
          {t('back')}
        </Button>
        <Box sx={{flex: '1 1 auto'}}/>
        <Tooltip
          title={
            isGoogleMapsBlocked
              ? t("departureTimeMustBeInFuture")
              : ""
          }
          disableHoverListener={!isGoogleMapsBlocked}
        >
          <Button
            color="primary"
            disabled={isDisabled}
            onClick={() => optimizeTemplates()}
            sx={{mr: 1}}
          >
            {t('createNewTemplates')}
          </Button>
        </Tooltip>
      </Fragment>
    );
  }

  function getTemplateComparisonButtons() {
    return (
      <Fragment>
        <Button color="primary" onClick={handleBack} sx={{mr: 1}}>
          {t('backToSelection')}
        </Button>
        <Box sx={{flex: '1 1 auto'}}/>
        <Button
          color="primary"
          disabled={selectedOptimizedTemplateIds.length === 0}
          onClick={importSelectedTemplates}
          sx={{mr: 1}}
        >
          {t('importSelectedTemplates')}
        </Button>
      </Fragment>
    );
  }

  return (
    <div className={classes.root}>
      <TemplateStopTemplateFilter
        carriersWithMicroHubs={carriersWithHubs}
        className={classes.filter}
        displayMode={displayMode}
        filter={filter}
        setFilter={setFilter}
        shipperOptions={shipperOptions}
      />
      <div className={classes.stepperContainer}>
        <Stepper activeStep={activeStep} className={classes.stepper} nonLinear>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepButton color="inherit" onClick={handleStep(index)}>
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
        <Box sx={{display: 'flex', flexDirection: 'row', pt: 2}}>
          {activeStep === 0 && getTemplateStopSelectionButtons()}
          {activeStep === 1 && getTemplateComparisonButtons()}
        </Box>
      </div>
      <LoadingWrapperWithTimer
        loading={
          backendLoadingTemplates ||
          backendLoadingTemplateStops ||
          backendLoadingFunctions
        }
        showTimer={backendLoadingFunctions}
        seconds={optimizerAlgorithmConfiguration.timeLimit}
      >
        <TextPlaceholderWrapper
          active={!filter.dayOfWeek || !filter.shipperName}
          text={t('tourTemplatesNoWeekDayAndShipperSelected')}
        >
          {' '}
          <Box sx={{pt: 2}}>{getStepContent(activeStep)}</Box>
        </TextPlaceholderWrapper>
      </LoadingWrapperWithTimer>
    </div>
  );
}

TemplateOptimization.propTypes = {
  classes: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  displayMode: PropTypes.oneOf(Object.values(displayModes)).isRequired,
};

export default compose(
  withStyles(styles),
  withTranslation()
)(TemplateOptimization);
